import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AtwReviewService } from '../../../../services/atw-review';

type ReviewStatus = 'active' | 'inactive' | 'pending';

interface AdminReview {
  id: number | string;
  nameEn: string;
  nameAr: string;
  textEn: string;
  textAr: string;
  rating: number;
  status: ReviewStatus;
  featured: boolean;
  userType: string;
  imageUrl: string;
  createdAt: string;
  raw: any;
}

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrl: './reviews.component.scss',
})
export class ReviewsComponent implements OnInit, OnDestroy {
  reviews: AdminReview[] = [];

  reviewForm!: FormGroup;
  selectedReview: AdminReview | null = null;

  readonly starItems: number[] = [1, 2, 3, 4, 5];

  page = 1;
  perPage = 10;
  total = 0;
  lastPage = 1;

  searchKeyword = '';
  statusFilter = '';
  userTypeFilter = '';

  isLoading = false;
  isSaving = false;
  errorMessage = '';
  showEditModal = false;

  isDeletingId: number | string | null = null;
  isChangingStatusId: number | string | null = null;

  isAr = false;

  private isBrowser: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private reviewService: AtwReviewService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.isAr = this.getCurrentLang() === 'ar';
    this.createForm();
    this.loadReviews(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pages(): number[] {
    return Array.from({ length: this.lastPage }, (_, index) => index + 1);
  }

  createForm(): void {
    this.reviewForm = this.fb.group({
      name_en: ['', [Validators.required]],
      name_ar: [''],
      text_en: ['', [Validators.required]],
      text_ar: [''],
      rating: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      status: ['active', [Validators.required]],
      user_type: ['guest', [Validators.required]],
      featured: [false],
    });
  }

  loadReviews(page: number = 1): void {
    if (page < 1) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.page = page;

    const filters: any = {};

    if (this.searchKeyword.trim()) {
      filters.q = this.searchKeyword.trim();
    }

    if (this.statusFilter) {
      filters.status = this.statusFilter;
    }

    if (this.userTypeFilter) {
      filters.user_type = this.userTypeFilter;
    }

    this.reviewService
      .getPaginatedReviews(this.page, this.perPage, filters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          const items = this.extractItems(response);

          this.reviews = items.map((item: any, index: number) =>
            this.mapReview(item, index)
          );

          this.total = this.extractTotal(response, items.length);
          this.lastPage = this.extractLastPage(response);
          this.page = this.extractCurrentPage(response, page);
        },
        error: () => {
          this.reviews = [];
          this.total = 0;
          this.lastPage = 1;
          this.errorMessage = 'Something went wrong while loading reviews.';
        },
      });
  }

  onSearchInput(event: Event): void {
    this.searchKeyword = (event.target as HTMLInputElement).value;
  }

  onStatusFilterChange(event: Event): void {
    this.statusFilter = (event.target as HTMLSelectElement).value;
    this.loadReviews(1);
  }

  onUserTypeFilterChange(event: Event): void {
    this.userTypeFilter = (event.target as HTMLSelectElement).value;
    this.loadReviews(1);
  }

  resetFilters(): void {
    this.searchKeyword = '';
    this.statusFilter = '';
    this.userTypeFilter = '';
    this.loadReviews(1);
  }

  openEditModal(review: AdminReview): void {
    this.selectedReview = review;

    this.reviewForm.patchValue({
      name_en: review.nameEn,
      name_ar: review.nameAr,
      text_en: review.textEn,
      text_ar: review.textAr,
      rating: review.rating,
      status: review.status,
      user_type: review.userType || 'guest',
      featured: review.featured,
    });

    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedReview = null;
    this.reviewForm.reset({
      name_en: '',
      name_ar: '',
      text_en: '',
      text_ar: '',
      rating: 5,
      status: 'active',
      user_type: 'guest',
      featured: false,
    });
  }

  submitEdit(): void {
    if (!this.selectedReview || this.reviewForm.invalid) {
      this.reviewForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    const formValue = this.reviewForm.value;

    const payload = {
      name_en: formValue.name_en,
      name_ar: formValue.name_ar,
      text_en: formValue.text_en,
      text_ar: formValue.text_ar,
      rating: Number(formValue.rating),
      status: formValue.status,
      user_type: formValue.user_type,
      featured: formValue.featured ? 1 : 0,
    };

    this.reviewService
      .updateReview(this.selectedReview.id, payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.closeEditModal();
          this.loadReviews(this.page);

          Swal.fire({
            icon: 'success',
            title: 'Updated',
            text: 'Review updated successfully.',
            timer: 1500,
            showConfirmButton: false,
          });
        },
        error: () => {
          Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: 'Could not update this review.',
          });
        },
      });
  }

  changeStatus(review: AdminReview, event: Event): void {
    const newStatus = (event.target as HTMLSelectElement).value as ReviewStatus;

    if (newStatus === review.status) {
      return;
    }

    const oldStatus = review.status;
    review.status = newStatus;

    this.isChangingStatusId = review.id;

    this.reviewService
      .updateReview(review.id, { status: newStatus })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isChangingStatusId = null;
        })
      )
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Status Updated',
            timer: 1000,
            showConfirmButton: false,
          });
        },
        error: () => {
          review.status = oldStatus;

          Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: 'Could not change review status.',
          });
        },
      });
  }

  toggleFeatured(review: AdminReview): void {
    const oldValue = review.featured;
    const newValue = !review.featured;

    review.featured = newValue;
    this.isChangingStatusId = review.id;

    this.reviewService
      .updateReview(review.id, { featured: newValue ? 1 : 0 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isChangingStatusId = null;
        })
      )
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Updated',
            timer: 1000,
            showConfirmButton: false,
          });
        },
        error: () => {
          review.featured = oldValue;

          Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: 'Could not update featured status.',
          });
        },
      });
  }

  deleteReview(review: AdminReview): void {
    Swal.fire({
      icon: 'warning',
      title: 'Delete Review?',
      text: 'This action cannot be undone.',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.isDeletingId = review.id;

      this.reviewService
        .deleteReview(review.id)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isDeletingId = null;
          })
        )
        .subscribe({
          next: () => {
            this.reviews = this.reviews.filter((item) => item.id !== review.id);

            Swal.fire({
              icon: 'success',
              title: 'Deleted',
              text: 'Review deleted successfully.',
              timer: 1500,
              showConfirmButton: false,
            });

            if (this.reviews.length === 0 && this.page > 1) {
              this.loadReviews(this.page - 1);
            } else {
              this.loadReviews(this.page);
            }
          },
          error: () => {
            Swal.fire({
              icon: 'error',
              title: 'Delete Failed',
              text: 'Could not delete this review.',
            });
          },
        });
    });
  }

  displayName(review: AdminReview): string {
    if (this.isAr) {
      return review.nameAr || review.nameEn || 'زائر';
    }

    return review.nameEn || review.nameAr || 'Guest';
  }

  displayText(review: AdminReview): string {
    if (this.isAr) {
      return review.textAr || review.textEn || '-';
    }

    return review.textEn || review.textAr || '-';
  }

  getAvatarLetter(name: string): string {
    const cleanName = (name || '').trim();

    if (!cleanName) {
      return '?';
    }

    return cleanName.charAt(0).toUpperCase();
  }

  onAvatarImageError(review: AdminReview): void {
    review.imageUrl = '';
  }

  formatDate(dateValue: string): string {
    if (!dateValue) {
      return '-';
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString(this.isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  trackByReview(index: number, review: AdminReview): number | string {
    return review.id || index;
  }

  private mapReview(item: any, index: number): AdminReview {
    const nameEn =
      this.cleanValue(item?.name_en) ||
      this.cleanValue(item?.reviewer_name_en) ||
      this.cleanValue(item?.client_name_en) ||
      this.cleanValue(item?.name) ||
      this.cleanValue(item?.reviewer_name) ||
      this.cleanValue(item?.client_name) ||
      this.cleanValue(item?.user_name) ||
      '';

    const nameAr =
      this.cleanValue(item?.name_ar) ||
      this.cleanValue(item?.reviewer_name_ar) ||
      this.cleanValue(item?.client_name_ar) ||
      '';

    const textEn =
      this.cleanValue(item?.text_en) ||
      this.cleanValue(item?.review_en) ||
      this.cleanValue(item?.comment_en) ||
      this.cleanValue(item?.message_en) ||
      this.cleanValue(item?.review_text) ||
      this.cleanValue(item?.text) ||
      this.cleanValue(item?.comment) ||
      this.cleanValue(item?.message) ||
      '';

    const textAr =
      this.cleanValue(item?.text_ar) ||
      this.cleanValue(item?.review_ar) ||
      this.cleanValue(item?.comment_ar) ||
      this.cleanValue(item?.message_ar) ||
      '';

    return {
      id: item?.id ?? index,
      nameEn,
      nameAr,
      textEn,
      textAr,
      rating: this.normalizeRating(item?.rating ?? item?.stars ?? item?.rate),
      status: this.normalizeStatus(item?.status),
      featured: this.normalizeBoolean(item?.featured ?? item?.is_featured),
      userType: this.cleanValue(item?.user_type) || 'guest',
      imageUrl:
        this.cleanValue(item?.image_url) ||
        this.cleanValue(item?.avatar_url) ||
        this.cleanValue(item?.photo_url) ||
        this.cleanValue(item?.image) ||
        this.cleanValue(item?.avatar) ||
        '',
      createdAt:
        this.cleanValue(item?.created_at) ||
        this.cleanValue(item?.createdAt) ||
        '',
      raw: item,
    };
  }

  private extractItems(response: any): any[] {
    const candidates = [
      response?.data?.data,
      response?.reviews?.data,
      response?.reviews,
      response?.data,
      response?.items,
      response,
    ];

    const found = candidates.find((candidate) => Array.isArray(candidate));

    return found || [];
  }

  private extractTotal(response: any, itemsLength: number): number {
    return Number(
      response?.data?.total ||
        response?.total ||
        response?.meta?.total ||
        response?.pagination?.total ||
        itemsLength
    );
  }

  private extractLastPage(response: any): number {
    const lastPage = Number(
      response?.data?.last_page ||
        response?.last_page ||
        response?.meta?.last_page ||
        response?.pagination?.last_page ||
        0
    );

    if (lastPage > 0) {
      return lastPage;
    }

    const total = this.extractTotal(response, this.reviews.length);

    return Math.max(Math.ceil(total / this.perPage), 1);
  }

  private extractCurrentPage(response: any, fallbackPage: number): number {
    return Number(
      response?.data?.current_page ||
        response?.current_page ||
        response?.meta?.current_page ||
        response?.pagination?.current_page ||
        fallbackPage
    );
  }

  private normalizeRating(value: any): number {
    const rating = Number(value || 5);

    if (Number.isNaN(rating)) {
      return 5;
    }

    return Math.min(Math.max(Math.round(rating), 1), 5);
  }

  private normalizeStatus(value: any): ReviewStatus {
    const status = this.cleanValue(value) as ReviewStatus;

    if (status === 'inactive' || status === 'pending' || status === 'active') {
      return status;
    }

    return 'pending';
  }

  private normalizeBoolean(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private cleanValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private getCurrentLang(): 'ar' | 'en' {
    if (!this.isBrowser) {
      return 'en';
    }

    return localStorage.getItem('language') === 'ar' ? 'ar' : 'en';
  }
}